import type { ReservationWrapper } from "@b2bportal/core-types";
import { Product } from "@b2bportal/purchase-api";
import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import { tripsApi } from "@/utils/commerce/client";
import type {
  GroundBookingContext,
  Trip,
  ProductOrderedGround,
} from "@/utils/commerce/types";

export interface ICarTripsResponse {
  itineraries: {
    upcoming: Array<ReservationWrapper<GroundBookingContext>>;
    past: Array<ReservationWrapper<GroundBookingContext>>;
  };
}

export const commerceTripsApi = createApi({
  reducerPath: "commerceTrips",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["CarReservationList"],
  endpoints: (builder) => ({
    fetchAllCarReservations: builder.query<ICarTripsResponse, void>({
      queryFn: async () => {
        try {
          const response = await tripsApi.getV1Trips();

          // Extract and wrap upcoming ground products
          const upcoming = (response.data.upcoming ?? []).flatMap(
            (trip: Trip) =>
              trip.productsOrdered
                .filter(
                  (p): p is ProductOrderedGround =>
                    p.ProductOrdered === "Ground"
                )
                .map(
                  (p): ReservationWrapper<GroundBookingContext> => ({
                    reservation: { ...p.bookingContext, tripId: trip.tripId },
                    reservationId: p.bookingContext.groundBookingId,
                    sortDate: p.bookingContext.productDetails.pickUp.dateTime,
                    recentFirst: false,
                    isCancelled: p.bookingContext.productState === "cancelled",
                    productType: Product.Ground,
                  })
                )
          );

          // Extract and wrap past ground products
          const past = (response.data.past ?? []).flatMap((trip: Trip) =>
            trip.productsOrdered
              .filter(
                (p): p is ProductOrderedGround => p.ProductOrdered === "Ground"
              )
              .map(
                (p): ReservationWrapper<GroundBookingContext> => ({
                  reservation: { ...p.bookingContext, tripId: trip.tripId },
                  reservationId: p.bookingContext.groundBookingId,
                  sortDate: p.bookingContext.productDetails.pickUp.dateTime,
                  recentFirst: true, // Past trips show most recent first
                  isCancelled: p.bookingContext.productState === "cancelled",
                  productType: Product.Ground,
                })
              )
          );

          return { data: { itineraries: { upcoming, past } } };
        } catch (error) {
          console.error("Failed to fetch car reservations:", error);
          return {
            error: error instanceof Error ? error : new Error("Unknown error"),
          };
        }
      },
      providesTags: ["CarReservationList"],
    }),
  }),
});
